import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import jsQR from 'jsqr';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';

const QrScanner = ({ active, onResult, onError }) => {
  const { t } = useTranslation('masterchart');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const decodedRef = useRef(false);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!active) return undefined;

    decodedRef.current = false;
    setStarting(true);
    setError(null);

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        await video.play();
        setStarting(false);
        tick();
      } catch (err) {
        // The key is kept, not the text, so the message follows a language switch.
        const key =
          err?.name === 'NotAllowedError'
            ? 'qrScanner.permissionDenied'
            : err?.name === 'NotFoundError'
              ? 'qrScanner.noCamera'
              : 'qrScanner.startFailed';
        setError(key);
        setStarting(false);
        onError?.(i18n.t(`masterchart:${key}`));
      }
    };

    const tick = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || decodedRef.current) return;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w && h) {
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, w, h);
          const imageData = ctx.getImageData(0, 0, w, h);
          const code = jsQR(imageData.data, w, h, { inversionAttempts: 'dontInvert' });
          if (code?.data) {
            decodedRef.current = true;
            onResult?.(code.data);
            return;
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    start();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const video = videoRef.current;
      if (video) video.srcObject = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        bgcolor: 'common.black',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        muted
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* viewfinder */}
      {!starting && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: '15%',
            border: '2px solid',
            borderColor: 'primary.light',
            borderRadius: 1,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
          }}
        />
      )}

      {starting && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1,
            color: 'common.white',
          }}
        >
          <CircularProgress size={28} sx={{ color: 'common.white' }} />
          <Typography variant="caption">{t('qrScanner.starting')}</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ position: 'absolute', inset: 0, p: 2, display: 'flex', alignItems: 'center' }}>
          <Alert severity="warning" sx={{ width: '100%' }}>
            {t(error)}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default QrScanner;
